import { useState, useEffect, useMemo } from 'react'

import { ActionIcon, Collapse, Loader, Select, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Check, Flag, X } from 'lucide-react'

import { navigate, routes } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DateInput from 'src/components/Inputs/DateInput/DateInput'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import {
  GET_LICENSE_INTELLIGENCE,
  GET_MVR_FINDINGS,
  REVIEW_DOCUMENT_PREDICTIONS,
} from 'src/graphql/documents.graphql'
import { DocumentCategory } from 'src/graphql/types/documents'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'
import IconSparkle from 'src/icons/IconSparkle'
import {
  getEndorsementsByLicenseClass,
  getLicenseClassOptions,
} from 'src/lib/license.utils'

import {
  MVR_COUNTED_FINDING_TYPES,
  mvrFindingsFromExtractedRows,
  MvrExtractedFinding,
} from 'src/lib/documentIntelligence'

import {
  bestPredictionsByField,
  cdlHasValue,
  evaluateRules,
  FLAG_RULES,
  medicalHasValue,
  MismatchSource,
  MvrScalarRow,
  RULE_KEY_TO_FORM_FIELD,
} from '../AiFlagsPill/aiFlagsRules'
import PlacesAutocomplete from '../AddressInput/PlacesAutocomplete'
import MultiSelectInput from '../MultiSelectInput/MultiSelectInput'
import SelectInput from '../SelectInput/SelectInput'
import TextInput from '../TextInput/TextInput'

dayjs.extend(utc)

// ── AI mismatch chip ─────────────────────────────────────────────────────────
// Shown next to a field label when AI extracted a different value than what
// appears on the application. The "Review" pill is imperative — it tells
// the recruiter to take a look, in the same pending-review (warning) color as
// the other AI finding surfaces. Tooltip lists each disagreeing source (DOT
// Med, MVR, CDL…) so the recruiter sees every value at once and the chip stays
// in sync with the AiFlagsPill dropdown row for the same field.
const AiMismatchChip = ({
  mismatches,
  onClick,
  onDismiss,
}: {
  mismatches: MismatchSource[]
  onClick?: () => void
  onDismiss?: () => void
}) => (
  <Tooltip
    label={
      <span className="flex flex-col gap-0.5 text-xs">
        <span>AI identified mismatch</span>
        {mismatches.map((m, i) => (
          <span
            key={`${m.sourceLabel}-${i}`}
            className="flex items-center gap-1 opacity-80"
          >
            <IconSparkle size={9} />
            {m.sourceLabel}: {m.aiValue}
          </span>
        ))}
        {onClick && <span className="opacity-60">Click to view document</span>}
      </span>
    }
    withArrow
    position="top"
  >
    <span
      className="ml-1 inline-flex items-center self-center overflow-hidden rounded-full border align-middle text-[10px] font-semibold leading-none"
      style={{
        backgroundColor: '#FFFAEB', // warning-50
        color: '#B54708', // warning-700
        borderColor: '#FEDF89', // warning-200
      }}
    >
      {/* Main label — navigates to document when clicked */}
      <span
        className={`flex items-center gap-0.5 px-1.5 py-px ${
          onClick
            ? 'cursor-pointer hover:bg-doubleNickel-warning-100'
            : 'cursor-default'
        }`}
        aria-label="AI identified mismatch — review needed"
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={(e) => {
          if (onClick) {
            e.stopPropagation()
            onClick()
          }
        }}
        onKeyDown={(e) => {
          if (onClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            e.stopPropagation()
            onClick()
          }
        }}
      >
        <Flag size={10} strokeWidth={2.5} className="shrink-0" />
      </span>
      {/* Dismiss button — marks prediction as KEPT_ORIGINAL */}
      {onDismiss && (
        <span
          className="flex cursor-pointer items-center border-l px-1 py-px hover:bg-doubleNickel-warning-100"
          style={{ borderColor: '#FEDF89' }}
          role="button"
          tabIndex={0}
          aria-label="Dismiss flag"
          onClick={(e) => {
            e.stopPropagation()
            onDismiss()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              onDismiss()
            }
          }}
        >
          <X size={9} strokeWidth={2.5} />
        </span>
      )}
    </span>
  </Tooltip>
)

const QuestionSelect = ({ label, editing, value, onChange }) => {
  const answer = value === undefined ? '-' : value ? 'Yes' : 'No'

  return (
    <div className="col-span-3 mt-3  grid grid-cols-3 items-center text-sm font-medium text-doubleNickel-gray-700">
      <div className="col-span-2">{label}</div>
      {editing ? (
        <Select
          className="col-span-1"
          placeholder="Select an option"
          value={value !== undefined ? value.toString() : undefined}
          data={[
            {
              value: 'true',
              label: 'Yes',
            },
            {
              value: 'false',
              label: 'No',
            },
          ]}
          onChange={(event) => {
            onChange(event === 'true')
          }}
        />
      ) : (
        <div className="text-sm font-semibold text-doubleNickel-gray-700">
          {answer}
        </div>
      )}
    </div>
  )
}

const LicenseInformationCard = ({
  values: appValues = [],
  handleUpdateApplication,
  applicantId,
}: {
  values: any[]
  handleUpdateApplication: (updates: any) => Promise<any>
  applicantId?: string
}) => {
  const [values, setValues] = useState({ ...appValues[0] })
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState(true)
  const [loading, setLoading] = useState(false)
  const [filteredEndorsements, setFilteredEndorsements] = useState([])
  const showEdorsements = values.country === 'US'

  // ── AI comparison ──────────────────────────────────────────────────────────
  const currentCompany = useCurrentCompanyContext()
  const showAiComparison =
    (currentCompany?.documentIntelligenceEnabled ?? false) && !!applicantId

  const { data: licenseIntelligenceData, refetch: refetchLicenseIntelligence } =
    useQuery(GET_LICENSE_INTELLIGENCE, {
      variables: { applicantId: applicantId ?? '' },
      skip: !showAiComparison,
      fetchPolicy: 'cache-and-network',
    })

  // MVR scalars (med cert expiration row) so the medical-expiration rule's
  // mvr-scalar source can fire on this card the same way it does in AiFlagsPill.
  // Without this, the engine sees only DOT Med and the card's chip silently
  // drops the MVR side of the disagreement even when both documents disagree.
  const { data: mvrData } = useQuery(GET_MVR_FINDINGS, {
    variables: { applicantId: applicantId ?? '' },
    skip: !showAiComparison,
    fetchPolicy: 'cache-and-network',
  })

  // Newest completed MVR's scalar rows (rows without `records`). Mirrors
  // AiFlagsPill — latest MVR is the source of truth, older reports are not
  // consulted.
  const latestMvrForScalars = useMemo<{
    rows: MvrScalarRow[]
    documentId: string | null
  }>(() => {
    if (!showAiComparison) return { rows: [], documentId: null }
    for (const doc of mvrData?.documents?.items ?? []) {
      if (doc.documentIntelligence?.status !== 'COMPLETED') continue
      const rows: MvrScalarRow[] = (
        doc.documentIntelligence.aiExtractedRows ?? []
      )
        .filter((row) => !row.records || row.records.length === 0)
        .map((row) => ({ field: row.field, value: row.value ?? null }))
      return { rows, documentId: doc.documentId }
    }
    return { rows: [], documentId: null }
  }, [mvrData, showAiComparison])

  // MVR existence findings (records-based rows like suspensions, license
  // actions) — used by the contradicted-absence rule for ENG-4273. Selection
  // mirrors AiFlagsPill: walk MVRs newest-first, return the first that carries
  // any surfaced-type findings.
  const selectedMvrForExistence = useMemo<{
    findings: MvrExtractedFinding[]
    documentId: string | null
  }>(() => {
    if (!showAiComparison) return { findings: [], documentId: null }
    for (const doc of mvrData?.documents?.items ?? []) {
      if (doc.documentIntelligence?.status !== 'COMPLETED') continue
      const f = mvrFindingsFromExtractedRows(
        doc.documentIntelligence.aiExtractedRows ?? []
      ).filter((finding) =>
        MVR_COUNTED_FINDING_TYPES.includes(finding.findingType)
      )
      if (f.length > 0) return { findings: f, documentId: doc.documentId }
    }
    return { findings: [], documentId: null }
  }, [mvrData, showAiComparison])

  const [reviewDocumentPredictions] = useMutation(REVIEW_DOCUMENT_PREDICTIONS, {
    onCompleted: () => refetchLicenseIntelligence(),
  })

  const dismissMismatch = (documentId: string | null, field: string) => {
    if (!documentId) return
    reviewDocumentPredictions({
      variables: {
        documentId,
        decisions: [{ field, decision: 'KEPT_ORIGINAL' }],
      },
    })
  }

  // Run the shared rule engine — same comparison logic AiFlagsPill uses. The
  // chip data shape preserves the FULL mismatches[] array per field so when
  // multiple sources disagree (e.g. both DOT Med and MVR contradict the
  // application's medical expiration date), the tooltip lists every source
  // instead of only the first. Doc-source mismatches stay byte-for-byte aligned
  // with the pill because both surfaces invoke the same engine.
  const { aiMismatches, cdlDocIds, medicalDocumentId, medicalHasDocSource } =
    useMemo(() => {
      if (!showAiComparison || !licenseIntelligenceData?.documents?.items) {
        return {
          aiMismatches: {} as Record<string, MismatchSource[]>,
          cdlDocIds: {} as Record<string, string | null>,
          medicalDocumentId: null as string | null,
          medicalHasDocSource: false,
        }
      }

      const docs = licenseIntelligenceData.documents.items
      const completedOfCategory = (category: DocumentCategory) =>
        docs.filter(
          (d) =>
            d.category === category &&
            d.documentIntelligence?.status === 'COMPLETED'
        )

      const flags = evaluateRules(FLAG_RULES, {
        license: {
          licenseNumber: values.licenseNumber,
          state: values.state,
          licenseClass: values.licenseClass,
          endorsements: values.endorsements,
          expirationDate: values.expirationDate
            ? typeof values.expirationDate === 'string'
              ? values.expirationDate
              : (values.expirationDate as Date).toISOString()
            : null,
          medicalExamExpirationDate: values.medicalExamExpirationDate
            ? typeof values.medicalExamExpirationDate === 'string'
              ? values.medicalExamExpirationDate
              : (values.medicalExamExpirationDate as Date).toISOString()
            : null,
          isLicenseSuspended: values.isLicenseSuspended,
        },
        cdlPredictions: bestPredictionsByField(
          completedOfCategory(DocumentCategory.CDL),
          cdlHasValue
        ),
        medicalPredictions: bestPredictionsByField(
          completedOfCategory(DocumentCategory.DOT_MEDICAL),
          medicalHasValue
        ),
        mvrScalarRows: latestMvrForScalars.rows,
        mvrScalarDocumentId: latestMvrForScalars.documentId,
        // Existence findings drive ENG-4273's license-ever-suspended rule, which
        // emits a ComparisonFlag (not a per-finding existence flag) — so the
        // card's existing comparison-flag pipeline picks it up. Per-finding
        // existence flags (accidents/violations rows) are still pill-territory
        // and ignored by the card's downstream loop.
        mvrFindings: selectedMvrForExistence.findings,
        mvrFindingsDocumentId: selectedMvrForExistence.documentId,
      })

      // Map Flag[] → per-field chip data. Each field carries the entire
      // mismatches[] array (one entry per disagreeing source) so the chip's
      // tooltip can list every source.
      //
      // Navigation target prefers a document source (CDL / DOT Med) so the chip
      // opens the DocumentEditScreen with the prediction in context. When no
      // document source exists — possible on medical-expiration when ONLY the
      // MVR scalar disagrees — we fall back to the first mismatch's documentId,
      // which routes the click to the MVR. Matches AiFlagsPill behavior; the
      // pre-fallback code left medicalDocumentId null and the click no-op'd.
      //
      // Dismiss (REVIEW_DOCUMENT_PREDICTIONS with KEPT_ORIGINAL) only makes
      // sense against a document source — MVR scalar rows don't flow through
      // the prediction review pipeline. We track a per-flag hasDocSource so the
      // medical chip can hide the dismiss × when there's nothing to dismiss.
      const mismatches: Record<string, MismatchSource[]> = {}
      const cdlDocIdMap: Record<string, string | null> = {}
      let medicalDocId: string | null = null
      let medicalHasDocSource = false

      for (const flag of flags) {
        if (flag.kind !== 'comparison') continue
        const formField = RULE_KEY_TO_FORM_FIELD[flag.key]
        if (!formField || flag.mismatches.length === 0) continue
        mismatches[formField] = flag.mismatches
        const firstDocSource = flag.mismatches.find((m) => m.predictionField)
        const navTarget =
          firstDocSource?.documentId ?? flag.mismatches[0]?.documentId ?? null
        if (flag.group === 'cdl') {
          // CDL rules only have CDL-document sources today, so firstDocSource is
          // always populated when there's a mismatch — no MVR fallback path.
          cdlDocIdMap[formField] = navTarget
        } else if (flag.group === 'medical') {
          medicalDocId = navTarget
          medicalHasDocSource = !!firstDocSource
        }
      }

      return {
        aiMismatches: mismatches,
        cdlDocIds: cdlDocIdMap,
        medicalDocumentId: medicalDocId,
        medicalHasDocSource,
      }
    }, [
      licenseIntelligenceData,
      latestMvrForScalars,
      selectedMvrForExistence,
      showAiComparison,
      values,
    ])

  const licenseClassData = getLicenseClassOptions(values.country)

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setValues({ ...appValues[0] })
    setEditing(false)
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      let updates = {}
      updates = { license: { ...values } }
      const result = await handleUpdateApplication(updates)
      if (!result.errors) {
        setEditing(false)
      }
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const availableEndorsements = getEndorsementsByLicenseClass(
      values.licenseClass
    )
    const currentEndorsements = values.endorsements || []
    const updatedEndorsements = currentEndorsements.filter((endorsement) =>
      availableEndorsements.includes(endorsement)
    )

    setFilteredEndorsements(getEndorsementsByLicenseClass(values.licenseClass))
    setValues({ ...values, endorsements: updatedEndorsements })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.licenseClass])

  const goToDocument = (documentId: string | null) => {
    if (!documentId || !applicantId) return
    navigate(
      routes.applicantDetails({
        id: applicantId,
        tab: 'documents',
        subtab: 'documents',
        documentId,
      })
    )
  }

  return (
    <div
      id="license-information-card"
      className="flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3"
    >
      <div
        className="text-md flex w-full cursor-pointer flex-row items-center gap-2 font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        License Information
        {!editing ? (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        ) : (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={loading}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
            {loading && <Loader size={12} color="gray" />}
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-2">
          <div className={`grid grid-cols-3 gap-2`}>
            <TextInput
              label={
                <>
                  License Number
                  {aiMismatches.licenseNumber && (
                    <AiMismatchChip
                      mismatches={aiMismatches.licenseNumber}
                      onClick={() =>
                        goToDocument(cdlDocIds.licenseNumber ?? null)
                      }
                      onDismiss={() =>
                        dismissMismatch(
                          cdlDocIds.licenseNumber ?? null,
                          'LICENSE_NUMBER'
                        )
                      }
                    />
                  )}
                </>
              }
              editing={editing}
              value={values.licenseNumber}
              handleChange={(value) =>
                setValues({ ...values, licenseNumber: value })
              }
            />

            {editing ? (
              <PlacesAutocomplete
                label={
                  <>
                    State
                    {aiMismatches.state && (
                      <AiMismatchChip
                        mismatches={aiMismatches.state}
                        onClick={() => goToDocument(cdlDocIds.state ?? null)}
                        onDismiss={() =>
                          dismissMismatch(
                            cdlDocIds.state ?? null,
                            'LICENSE_STATE'
                          )
                        }
                      />
                    )}
                  </>
                }
                placeholder="Select a state from the dropdown"
                value={values}
                types={['administrative_area_level_1']}
                handleChange={(value) => {
                  const countryChanged = value.country !== values.country
                  setValues({
                    ...values,
                    state: value.state,
                    country: value.country,
                    ...(countryChanged && { licenseClass: null }),
                    ...(countryChanged && { endorsements: [] }),
                  })
                }}
              />
            ) : (
              <StyledReadOnlyInput
                label={
                  <>
                    State
                    {aiMismatches.state && (
                      <AiMismatchChip
                        mismatches={aiMismatches.state}
                        onClick={() => goToDocument(cdlDocIds.state ?? null)}
                        onDismiss={() =>
                          dismissMismatch(
                            cdlDocIds.state ?? null,
                            'LICENSE_STATE'
                          )
                        }
                      />
                    )}
                  </>
                }
                value={values.state}
              />
            )}

            <SelectInput
              label={
                <>
                  License Class
                  {aiMismatches.licenseClass && (
                    <AiMismatchChip
                      mismatches={aiMismatches.licenseClass}
                      onClick={() =>
                        goToDocument(cdlDocIds.licenseClass ?? null)
                      }
                      onDismiss={() =>
                        dismissMismatch(
                          cdlDocIds.licenseClass ?? null,
                          'LICENSE_CLASS'
                        )
                      }
                    />
                  )}
                </>
              }
              editing={editing}
              value={values.licenseClass}
              data={licenseClassData}
              handleChange={(value) => {
                setValues({ ...values, licenseClass: value })
              }}
            />
            {showEdorsements && (
              <>
                {editing ? (
                  <MultiSelectInput
                    label={
                      <>
                        Endorsements
                        {aiMismatches.endorsements && (
                          <AiMismatchChip
                            mismatches={aiMismatches.endorsements}
                            onClick={() =>
                              goToDocument(cdlDocIds.endorsements ?? null)
                            }
                            onDismiss={() =>
                              dismissMismatch(
                                cdlDocIds.endorsements ?? null,
                                'LICENSE_ENDORSEMENTS'
                              )
                            }
                          />
                        )}
                      </>
                    }
                    editing={editing}
                    value={values.endorsements}
                    data={filteredEndorsements}
                    handleChange={(endorsements) => {
                      setValues({ ...values, endorsements })
                    }}
                  />
                ) : (
                  <StyledReadOnlyInput
                    label={
                      <>
                        Endorsements
                        {aiMismatches.endorsements && (
                          <AiMismatchChip
                            mismatches={aiMismatches.endorsements}
                            onClick={() =>
                              goToDocument(cdlDocIds.endorsements ?? null)
                            }
                            onDismiss={() =>
                              dismissMismatch(
                                cdlDocIds.endorsements ?? null,
                                'LICENSE_ENDORSEMENTS'
                              )
                            }
                          />
                        )}
                      </>
                    }
                    value={values.endorsements?.join(', ')}
                  />
                )}
              </>
            )}

            <DateInput
              label={
                <>
                  Expiration Date
                  {aiMismatches.expirationDate && (
                    <AiMismatchChip
                      mismatches={aiMismatches.expirationDate}
                      onClick={() =>
                        goToDocument(cdlDocIds.expirationDate ?? null)
                      }
                      onDismiss={() =>
                        dismissMismatch(
                          cdlDocIds.expirationDate ?? null,
                          'DATE'
                        )
                      }
                    />
                  )}
                </>
              }
              editing={editing}
              value={values.expirationDate}
              onChange={(value) => {
                setValues({ ...values, expirationDate: value })
              }}
            />

            <DateInput
              label={
                <>
                  Medical Exp. Date
                  {aiMismatches.medicalExamExpirationDate && (
                    <AiMismatchChip
                      mismatches={aiMismatches.medicalExamExpirationDate}
                      onClick={() => goToDocument(medicalDocumentId)}
                      onDismiss={
                        medicalHasDocSource
                          ? () => dismissMismatch(medicalDocumentId, 'DATE')
                          : undefined
                      }
                    />
                  )}
                </>
              }
              editing={editing}
              value={values.medicalExamExpirationDate}
              onChange={(value) => {
                setValues({ ...values, medicalExamExpirationDate: value })
              }}
            />

            <QuestionSelect
              label={
                <>
                  Have they had any license, permit, or privilege ever been
                  suspended, revoked, or denied?
                  {aiMismatches.isLicenseSuspended && (
                    <AiMismatchChip
                      mismatches={aiMismatches.isLicenseSuspended}
                      onClick={() =>
                        goToDocument(
                          aiMismatches.isLicenseSuspended[0]?.documentId ?? null
                        )
                      }
                    />
                  )}
                </>
              }
              editing={editing}
              value={values.isLicenseSuspended}
              onChange={(value) => {
                if (value) {
                  setValues({ ...values, isLicenseSuspended: value })
                } else {
                  setValues({
                    ...values,
                    isLicenseSuspended: value,
                    licenseSuspendedReason: '',
                  })
                }
              }}
            />
            {values.isLicenseSuspended && (
              <TextInput
                className="col-span-3"
                label="Reason for the suspension:"
                editing={editing}
                value={values.licenseSuspendedReason}
                handleChange={(value) =>
                  setValues({ ...values, licenseSuspendedReason: value })
                }
              />
            )}
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default LicenseInformationCard
